import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { menuForRole } from "@/components/menu";
import { Menu, LogOut, Search, Bell, ShieldAlert } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

function Brand({ small }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 border border-[rgba(0,200,255,0.5)]"
        style={{ background: "linear-gradient(135deg, rgba(6,22,46,0.9), rgba(3,10,24,0.95))", boxShadow: "0 0 16px rgba(0,200,255,0.35)" }}>
        <span className="font-head font-black text-[var(--gold)] text-xl leading-none" style={{ textShadow: "0 0 10px rgba(245,197,66,0.6)" }}>T</span>
      </div>
      {!small && (
        <div className="leading-tight">
          <div className="font-head font-extrabold text-[15px] tracking-tight text-white">TITANIUM PARTS</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--neon-cyan)]">SPK • Receiving • Warehouse</div>
        </div>
      )}
    </div>
  );
}

function NavItems({ role, onNavigate }) {
  const items = menuForRole(role);
  const { pathname } = useLocation();
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) activeRef.current.scrollIntoView({ block: "nearest" });
  }, [pathname]);

  return (
    <nav className="flex flex-col gap-1.5 px-3">
      {items.map((m) => {
        const Icon = m.icon;
        const active = pathname === m.to || pathname.startsWith(m.to + "/");
        return (
          <NavLink
            key={m.to}
            to={m.to}
            ref={active ? activeRef : null}
            onClick={onNavigate}
            data-testid={`nav-${m.to.replace("/", "")}`}
            className={({ isActive }) =>
              `cyber-tile flex items-center gap-3 px-3.5 min-h-[48px] py-2.5 text-sm ${isActive ? "cyber-tile-active" : ""}`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={18} strokeWidth={2} className={isActive ? "text-white" : "text-[var(--neon-cyan)]"} />
                <span>{m.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 sidebar-glow z-30">
        <div className="h-16 flex items-center px-5 border-b border-[rgba(0,200,255,0.18)]">
          <Brand />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto py-4">
          <NavItems role={user.role} />
        </div>
        <div className="p-3 border-t border-[rgba(0,200,255,0.18)]">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-xs text-[var(--neon-cyan)] truncate">{user.role_label}</div>
          </div>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid="logout-btn"
            className="w-full justify-start text-gray-600 hover:text-red-400 hover:bg-red-500/10"
          >
            <LogOut size={16} className="mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 h-16 bg-white/85 backdrop-blur-md border-b border-gray-200 flex items-center gap-3 px-4">
          {/* mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -ml-1 rounded-xl cyber-tile" data-testid="mobile-menu-btn">
                <Menu size={22} className="text-[var(--neon-cyan)]" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 sidebar-glow border-r border-[rgba(0,200,255,0.3)] flex flex-col" style={{ height: "100dvh" }}>
              <div className="h-16 shrink-0 flex items-center px-5 border-b border-[rgba(0,200,255,0.18)]">
                <Brand />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-4">
                <NavItems role={user.role} onNavigate={() => setOpen(false)} />
              </div>
              <div className="shrink-0 p-3 border-t border-[rgba(0,200,255,0.18)]" style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom))" }}>
                <div className="px-3 py-1 text-sm font-semibold text-white">{user.name}</div>
                <div className="px-3 text-xs text-[var(--neon-cyan)] mb-2">{user.role_label}</div>
                <Button variant="ghost" onClick={logout} className="w-full justify-start text-red-400">
                  <LogOut size={16} className="mr-2" /> Keluar
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          <div className="lg:hidden"><Brand small /></div>

          <form onSubmit={doSearch} className="flex-1 max-w-md ml-auto lg:ml-0">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--neon-cyan)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="global-search-input"
                placeholder="Cari Nopol, No Claim, No SPK, Part..."
                className="neon-input w-full h-11 pl-11 pr-4 rounded-full text-sm"
              />
            </div>
          </form>

          <NavLink to="/notifications" className="p-2.5 rounded-xl cyber-tile relative" data-testid="notif-btn">
            <Bell size={20} className="text-[var(--neon-cyan)]" />
          </NavLink>
        </header>

        <main className="p-4 sm:p-6 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="access-denied">
      <div className="h-16 w-16 rounded-full flex items-center justify-center mb-4 border border-[rgba(255,80,110,0.5)]"
        style={{ background: "rgba(255,60,90,0.12)", boxShadow: "0 0 20px rgba(255,80,110,0.4)" }}>
        <ShieldAlert size={32} className="text-[#FF8CA3]" />
      </div>
      <h2 className="font-head text-2xl font-bold text-white glow-text">ACCESS DENIED</h2>
      <p className="text-gray-500 mt-2 max-w-sm">
        Anda tidak memiliki izin untuk mengakses halaman ini. Menu ini hanya untuk role tertentu.
      </p>
    </div>
  );
}
